import i18n, { type InitOptions } from "i18next";
import { initReactI18next } from "react-i18next";
import HttpBackend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";

export const SUPPORTED_LANGUAGES = ["en", "zh"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// Exported so tests can init the same real i18next singleton against this
// exact literal instead of a hand-copied duplicate — see src/testI18n.ts.
export const i18nOptions: InitOptions = {
  fallbackLng: "en",
  supportedLngs: [...SUPPORTED_LANGUAGES],
  // With supportedLngs: ["en", "zh"], i18next's own resolvedLanguage
  // fallback already maps a browser advertising zh-CN to `zh`, so this flag
  // is inert today. It becomes load-bearing the day a regional variant
  // that doesn't already collapse to a supported language is added — e.g.
  // zh-TW would otherwise fall through to English instead of `zh`.
  nonExplicitSupportedLngs: true,
  load: "languageOnly",
  debug: process.env.NODE_ENV === "development",
  ns: ["common", "marketing", "legal", "billing", "auth"],
  defaultNS: "common",
  interpolation: { escapeValue: false },
  backend: { loadPath: "/locales/{{lng}}/{{ns}}.json" },
  detection: {
    order: ["querystring", "localStorage", "navigator", "htmlTag"],
    lookupQuerystring: "lang",
    caches: ["localStorage"],
  },
  react: { useSuspense: true },
};

// Exported so tests can await the same init this module triggers as a side
// effect, instead of guessing when the singleton is ready.
export const i18nReady = i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init(i18nOptions);

// Keeps assistive technology and the browser's own translation prompt in
// step with what the user actually chose.
i18n.on("languageChanged", (lng) => {
  document.documentElement.lang = lng;
});

export default i18n;
